import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Image, FileText, Layers, ChevronLeft } from 'lucide-react';
import { Badge } from '../../components/ui/Badge/Badge';
import { Button } from '../../components/ui/Button/Button';
import { Table } from '../../components/ui/Table';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Modal } from '../../components/ui/Modal/Modal';
import { Input } from '../../components/ui/Input/Input';
import { Select } from '../../components/ui/Select/Select';
import { Avatar } from '../../components/ui/Avatar/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { apiFetch } from '@/lib/api';
import { useNotificationStore } from '@/stores/notificationStore';
import type { CreditGrant, CreditTransaction } from '@/services/CreditService';

const formatCredits = (v: unknown) => `${Number(v || 0).toLocaleString()} credits`;
const formatCreditsShort = (v: unknown) => Number(v || 0).toLocaleString();
const formatUsd = (v: unknown) => `$${Number(v || 0).toFixed(4)}`;
const formatUsd2 = (v: unknown) => `$${Number(v || 0).toFixed(2)}`;

export function OrgDetails() {
  const { id } = useParams();
  const notify = useNotificationStore();
  const [loading, setLoading] = React.useState(false);
  const [org, setOrg] = React.useState<any>(null);
  const [invites, setInvites] = React.useState<Array<any>>([]);
  const [members, setMembers] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);

  // Add User modal
  const [showAddUserModal, setShowAddUserModal] = React.useState(false);
  const [addUserEmail, setAddUserEmail] = React.useState('');
  const [addUserRole, setAddUserRole] = React.useState('member');
  const [addUserLoading, setAddUserLoading] = React.useState(false);
  const [addUserError, setAddUserError] = React.useState<string | null>(null);
  const [addUserResult, setAddUserResult] = React.useState<{ type: string; message: string } | null>(null);

  // Onboarding toggle
  const [onboardingToggling, setOnboardingToggling] = React.useState(false);

  // Credits state
  const [creditBalance, setCreditBalance] = React.useState<number>(0);
  const [creditGrants, setCreditGrants] = React.useState<CreditGrant[]>([]);
  const [creditTx, setCreditTx] = React.useState<CreditTransaction[]>([]);
  const [creditTxTotal, setCreditTxTotal] = React.useState(0);
  const [creditTxPage, setCreditTxPage] = React.useState(0);
  const [creditsLoading, setCreditsLoading] = React.useState(false);
  const [grantAmount, setGrantAmount] = React.useState('');
  const [grantDays, setGrantDays] = React.useState('90');
  const [grantDesc, setGrantDesc] = React.useState('');
  const [grantSubmitting, setGrantSubmitting] = React.useState(false);

  // LLM usage state
  const [llmSummary, setLlmSummary] = React.useState<any>(null);
  const [llmLogs, setLlmLogs] = React.useState<any[]>([]);
  const [llmLogsTotal, setLlmLogsTotal] = React.useState(0);
  const [llmLogsPage, setLlmLogsPage] = React.useState(1);
  const [llmLogsLoading, setLlmLogsLoading] = React.useState(false);
  const [llmPurpose, setLlmPurpose] = React.useState('');

  // Active tab
  const [activeTab, setActiveTab] = React.useState<'overview' | 'credits' | 'usage'>('overview');

  const loadData = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [orgRes, invRes, memRes] = await Promise.all([
        fetch(`/api/admin/orgs/${encodeURIComponent(id)}`, { credentials: 'include' }),
        fetch(`/api/admin/orgs/${encodeURIComponent(id)}/invites`, { credentials: 'include' }),
        fetch(`/api/admin/orgs/${encodeURIComponent(id)}/members`, { credentials: 'include' }),
      ]);
      const orgJson = await orgRes.json();
      if (!orgJson.success) throw new Error(orgJson.error?.message || 'Failed to load org');
      const invJson = await invRes.json();
      const memJson = await memRes.json();
      setOrg(orgJson.data);
      setInvites(invJson.success ? invJson.data || [] : []);
      setMembers(memJson.success ? memJson.data || [] : []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCredits = React.useCallback(async () => {
    if (!id) return;
    setCreditsLoading(true);
    try {
      const [sumRes, grantsRes, txRes] = await Promise.all([
        apiFetch(`/api/admin/credits/${id}`).then(r => r.json()),
        apiFetch(`/api/admin/credits/${id}/grants`).then(r => r.json()),
        apiFetch(`/api/admin/credits/${id}/transactions?limit=20&offset=${creditTxPage * 20}`).then(r => r.json()),
      ]);
      setCreditBalance(Number(sumRes.data?.balance?.balanceCredits) || 0);
      setCreditGrants(grantsRes.data || []);
      setCreditTx(txRes.data?.items || []);
      setCreditTxTotal(txRes.data?.total || 0);
    } catch {}
    setCreditsLoading(false);
  }, [id, creditTxPage]);

  const loadLlmSummary = React.useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiFetch(`/api/admin/orgs/${id}/llm-summary`).then(r => r.json());
      if (res.success) setLlmSummary(res.data);
    } catch {}
  }, [id]);

  const loadLlmLogs = React.useCallback(async () => {
    if (!id) return;
    setLlmLogsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(llmLogsPage), pageSize: '20' });
      if (llmPurpose) params.set('purpose', llmPurpose);
      const res = await apiFetch(`/api/admin/orgs/${id}/llm-logs?${params}`).then(r => r.json());
      if (res.success) {
        setLlmLogs(res.data.items || []);
        setLlmLogsTotal(res.data.total || 0);
      }
    } catch {}
    setLlmLogsLoading(false);
  }, [id, llmLogsPage, llmPurpose]);

  React.useEffect(() => { loadData(); }, [loadData]);
  React.useEffect(() => { if (activeTab === 'credits') loadCredits(); }, [activeTab, loadCredits]);
  React.useEffect(() => {
    if (activeTab === 'usage') { loadLlmSummary(); loadLlmLogs(); }
  }, [activeTab, loadLlmSummary, loadLlmLogs]);

  const handleGrant = async () => {
    if (!id || !grantAmount || !grantDesc.trim()) {
      notify.error('Amount and description required');
      return;
    }
    setGrantSubmitting(true);
    try {
      const res = await apiFetch('/api/admin/credits/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId: id,
          amountCredits: parseInt(grantAmount, 10),
          expiresInDays: parseInt(grantDays, 10),
          description: grantDesc,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Grant failed');
      notify.success(`Granted ${Number(grantAmount).toLocaleString()} credits`);
      setGrantAmount('');
      setGrantDesc('');
      loadCredits();
    } catch (err: any) {
      notify.error(err.message);
    }
    setGrantSubmitting(false);
  };

  const handleAddUser = async () => {
    if (!addUserEmail.trim() || !id) return;
    setAddUserLoading(true);
    setAddUserError(null);
    setAddUserResult(null);
    try {
      const res = await fetch(`/api/admin/orgs/${encodeURIComponent(id)}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: addUserEmail.trim().toLowerCase(), role: addUserRole }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to add user');
      const resultType = json.data.type;
      setAddUserResult({
        type: resultType,
        message: resultType === 'member_added'
          ? `${addUserEmail} added as ${addUserRole}`
          : `Invite created for ${addUserEmail} (user not found in system)`,
      });
      setAddUserEmail('');
      setAddUserRole('member');
      await loadData();
    } catch (e: any) {
      setAddUserError(e.message);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!id || !confirm('Remove this member from the organization?')) return;
    try {
      const res = await fetch(`/api/admin/orgs/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to remove member');
      await loadData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    if (!id) return;
    try {
      const res = await fetch(`/api/admin/orgs/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to update role');
      await loadData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleToggleOnboarding = async () => {
    if (!id || !org) return;
    setOnboardingToggling(true);
    try {
      const res = await fetch(`/api/admin/orgs/${encodeURIComponent(id)}/onboarding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completed: !org.onboarding_completed }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to toggle onboarding');
      await loadData();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setOnboardingToggling(false);
    }
  };

  if (!id) return null;

  const memberColumns = [
    {
      key: 'full_name',
      title: 'Member',
      render: (_: any, record: any) => (
        <div className="flex items-center gap-3">
          <Avatar name={record.full_name || record.email} size="sm" />
          <div>
            <div className="font-medium text-gray-900">{record.full_name || '(no name)'}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      title: 'Role',
      render: (value: string, record: any) => (
        <Select
          value={value}
          options={[
            { value: 'owner', label: 'Owner' },
            { value: 'admin', label: 'Admin' },
            { value: 'member', label: 'Member' },
          ]}
          onValueChange={(newRole) => handleChangeRole(record.user_id, newRole)}
        />
      ),
    },
    {
      key: 'joined_at',
      title: 'Joined',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <Button size="sm" variant="danger" onClick={() => handleRemoveMember(record.user_id)}>
          Remove
        </Button>
      ),
    },
  ];

  const inviteColumns = [
    { key: 'email', title: 'Email', sortable: true },
    { key: 'role', title: 'Role', render: (value: string) => <Badge variant="outline">{value}</Badge> },
    {
      key: 'status', title: 'Status',
      render: (_: any, record: any) => {
        const isAccepted = record.accepted_at;
        const isExpired = new Date(record.expires_at) < new Date();
        if (isAccepted) return <Badge variant="success">Accepted</Badge>;
        if (isExpired) return <Badge variant="error">Expired</Badge>;
        return <Badge variant="warning">Pending</Badge>;
      }
    },
    {
      key: 'created_at', title: 'Sent', sortable: true,
      render: (value: string) => {
        const d = new Date(value);
        return <div><div className="text-sm">{d.toLocaleDateString()}</div><div className="text-xs text-gray-500">{d.toLocaleTimeString()}</div></div>;
      }
    },
    {
      key: 'expires_at', title: 'Expires', sortable: true,
      render: (value: string) => {
        const d = new Date(value);
        const isExpired = d < new Date();
        return <div className={isExpired ? 'text-red-600' : ''}><div className="text-sm">{d.toLocaleDateString()}</div><div className="text-xs text-gray-500">{d.toLocaleTimeString()}</div></div>;
      }
    }
  ];

  const statCards = org?.stats ? [
    { title: 'Members', value: org.memberCount, color: 'bg-blue-50 text-blue-600', icon: <Users className="w-5 h-5" /> },
    { title: 'Assets', value: org.stats.assetsCount, color: 'bg-green-50 text-green-600', icon: <Image className="w-5 h-5" /> },
    { title: 'Templates', value: org.stats.templatesCount, color: 'bg-purple-50 text-purple-600', icon: <FileText className="w-5 h-5" /> },
    { title: 'Gallery Items', value: org.stats.galleryCount, color: 'bg-amber-50 text-amber-600', icon: <Layers className="w-5 h-5" /> },
  ] : [];

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'credits', label: 'Credits' },
    { key: 'usage', label: 'LLM Usage' },
  ] as const;

  return (
    <PageContainer>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link to="/admin/orgs" className="text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" />
            </Link>
            <span className="text-sm text-gray-500">Organizations</span>
          </div>
          {org && (
            <PageHeader title={org.name} className="mb-2">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">{org.slug}</span>
                <Badge variant={org.status === 'active' ? 'success' : org.status === 'suspended' ? 'error' : 'warning'}>{org.status}</Badge>
              </div>
            </PageHeader>
          )}
        </div>
        {org && (
          <div className="text-right">
            <div className="text-sm text-gray-500">Created</div>
            <div className="text-sm font-medium">{new Date(org.created_at).toLocaleDateString()}</div>
          </div>
        )}
      </div>

      {loading && <div className="text-center py-8 text-gray-500">Loading...</div>}
      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-md"><div className="text-sm text-red-600">{error}</div></div>}

      {org && (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200 mt-4">
            <nav className="flex gap-6">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* ═══ OVERVIEW TAB ═══ */}
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
                {statCards.map((card, i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{card.title}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                      </div>
                      <div className={`p-2 rounded-lg ${card.color}`}>{card.icon}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization Details</h2>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><dt className="text-sm font-medium text-gray-500">Name</dt><dd className="text-sm text-gray-900">{org.name}</dd></div>
                  <div><dt className="text-sm font-medium text-gray-500">Slug</dt><dd className="text-sm text-gray-900">{org.slug}</dd></div>
                  <div><dt className="text-sm font-medium text-gray-500">Status</dt><dd><Badge variant={org.status === 'active' ? 'success' : org.status === 'suspended' ? 'error' : 'warning'}>{org.status}</Badge></dd></div>
                  <div><dt className="text-sm font-medium text-gray-500">Plan</dt><dd className="text-sm text-gray-900">{org.plan_key || 'Free'}</dd></div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Onboarding</dt>
                    <dd className="flex items-center gap-2">
                      <Badge variant={org.onboarding_completed ? 'success' : 'warning'}>{org.onboarding_completed ? 'Completed' : 'Not Completed'}</Badge>
                      <Button size="sm" variant="outline" onClick={handleToggleOnboarding} isLoading={onboardingToggling}>
                        {org.onboarding_completed ? 'Mark Incomplete' : 'Mark Complete'}
                      </Button>
                    </dd>
                  </div>
                  {org.trial_ends_at && <div><dt className="text-sm font-medium text-gray-500">Trial Ends</dt><dd className="text-sm text-gray-900">{new Date(org.trial_ends_at).toLocaleDateString()}</dd></div>}
                  <div><dt className="text-sm font-medium text-gray-500">Created</dt><dd className="text-sm text-gray-900">{new Date(org.created_at).toLocaleString()}</dd></div>
                </dl>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Members</h2>
                  <Button size="sm" onClick={() => { setShowAddUserModal(true); setAddUserResult(null); setAddUserError(null); }}>Add User</Button>
                </div>
                <Table data={members} columns={memberColumns} searchPlaceholder="Search members..." pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 20] }} emptyText="No members found." />
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Invitations</h2>
                <Table data={invites} columns={inviteColumns} searchPlaceholder="Search invites by email..." pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 20] }} emptyText="No invitations found." />
              </div>
            </>
          )}

          {/* ═══ CREDITS TAB ═══ */}
          {activeTab === 'credits' && (
            <>
              {creditsLoading && !creditGrants.length ? (
                <div className="flex justify-center py-12"><Spinner size="lg" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Credit Balance</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{formatCreditsShort(creditBalance)}</div><div className="text-sm text-muted-foreground mt-1">credits</div></CardContent></Card>
                    <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Active Grants</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{creditGrants.filter(g => Number(g.remaining_credits) > 0 && new Date(g.expires_at) > new Date()).length}</div></CardContent></Card>
                    <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Granted</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{formatCreditsShort(creditGrants.reduce((sum, g) => sum + Number(g.amount_credits || 0), 0))}</div><div className="text-sm text-muted-foreground mt-1">credits</div></CardContent></Card>
                  </div>

                  <Card className="mt-6">
                    <CardHeader><CardTitle>Grant Credits</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-3 items-end">
                        <div className="w-32">
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Amount (Credits)</label>
                          <Input type="number" step="1" min="1" value={grantAmount} onChange={e => setGrantAmount(e.target.value)} placeholder="2000" />
                        </div>
                        <div className="w-28">
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Expires (days)</label>
                          <Input type="number" min="1" max="365" value={grantDays} onChange={e => setGrantDays(e.target.value)} />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                          <Input value={grantDesc} onChange={e => setGrantDesc(e.target.value)} placeholder="Alpha welcome grant" />
                        </div>
                        <Button onClick={handleGrant} disabled={grantSubmitting} size="sm">
                          {grantSubmitting ? 'Granting...' : 'Grant'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {creditGrants.length > 0 && (
                    <Card className="mt-6">
                      <CardHeader><CardTitle>All Grants</CardTitle></CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead><tr className="border-b text-left text-muted-foreground">
                              <th className="py-2 pr-3">Description</th><th className="py-2 pr-3">Remaining</th><th className="py-2 pr-3">Original</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Expires</th><th className="py-2">Created</th>
                            </tr></thead>
                            <tbody>
                              {creditGrants.map(g => {
                                const remaining = Number(g.remaining_credits);
                                const expired = new Date(g.expires_at) < new Date();
                                const depleted = remaining <= 0;
                                return (
                                  <tr key={g.id} className="border-b last:border-0">
                                    <td className="py-2 pr-3">{g.description || '—'}</td>
                                    <td className="py-2 pr-3 font-medium">{formatCredits(g.remaining_credits)}</td>
                                    <td className="py-2 pr-3 text-muted-foreground">{formatCredits(g.amount_credits)}</td>
                                    <td className="py-2 pr-3">
                                      {expired ? <Badge variant="error">Expired</Badge> : depleted ? <Badge variant="secondary">Depleted</Badge> : <Badge variant="success">Active</Badge>}
                                    </td>
                                    <td className="py-2 pr-3">{new Date(g.expires_at).toLocaleDateString()}</td>
                                    <td className="py-2">{new Date(g.created_at).toLocaleDateString()}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Card className="mt-6">
                    <CardHeader><CardTitle>Credit Transactions</CardTitle></CardHeader>
                    <CardContent>
                      {creditTx.length === 0 ? (
                        <p className="text-center text-muted-foreground py-6">No transactions yet.</p>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead><tr className="border-b text-left text-muted-foreground">
                                <th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Description</th><th className="py-2">Date</th>
                              </tr></thead>
                              <tbody>
                                {creditTx.map(tx => (
                                  <tr key={tx.id} className="border-b last:border-0">
                                    <td className="py-2 pr-3"><Badge variant={tx.type === 'grant' ? 'success' : tx.type === 'expiry' ? 'warning' : 'secondary'}>{tx.type}</Badge></td>
                                    <td className="py-2 pr-3 font-medium">{tx.type === 'debit' ? '-' : ''}{formatCredits(tx.amount_credits)}</td>
                                    <td className="py-2 pr-3">{tx.description || '—'}</td>
                                    <td className="py-2 whitespace-nowrap">{new Date(tx.created_at).toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {creditTxTotal > 20 && (
                            <div className="flex items-center justify-between mt-4">
                              <span className="text-sm text-muted-foreground">{creditTxPage * 20 + 1}–{Math.min((creditTxPage + 1) * 20, creditTxTotal)} of {creditTxTotal}</span>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" disabled={creditTxPage === 0} onClick={() => setCreditTxPage(p => p - 1)}>Prev</Button>
                                <Button size="sm" variant="outline" disabled={(creditTxPage + 1) * 20 >= creditTxTotal} onClick={() => setCreditTxPage(p => p + 1)}>Next</Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}

          {/* ═══ LLM USAGE TAB ═══ */}
          {activeTab === 'usage' && (
            <>
              {llmSummary?.totals && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                  <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total API Calls</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{Number(llmSummary.totals.total_calls || 0).toLocaleString()}</div></CardContent></Card>
                  <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Cost</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{formatUsd2(llmSummary.totals.total_cost)}</div></CardContent></Card>
                  <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Input Tokens</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{Number(llmSummary.totals.total_input_tokens || 0).toLocaleString()}</div></CardContent></Card>
                  <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Output Tokens</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{Number(llmSummary.totals.total_output_tokens || 0).toLocaleString()}</div></CardContent></Card>
                </div>
              )}

              {llmSummary?.breakdown?.length > 0 && (
                <Card className="mt-6">
                  <CardHeader><CardTitle>Usage by Model</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b text-left text-muted-foreground">
                          <th className="py-2 pr-3">Model</th><th className="py-2 pr-3">Purpose</th><th className="py-2 pr-3 text-right">Calls</th><th className="py-2 pr-3 text-right">Cost</th><th className="py-2 pr-3 text-right">Input Tokens</th><th className="py-2 text-right">Output Tokens</th>
                        </tr></thead>
                        <tbody>
                          {llmSummary.breakdown.map((row: any, i: number) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="py-2 pr-3 font-medium">{row.model}</td>
                              <td className="py-2 pr-3"><Badge variant="outline">{row.purpose}</Badge></td>
                              <td className="py-2 pr-3 text-right">{Number(row.count).toLocaleString()}</td>
                              <td className="py-2 pr-3 text-right font-medium">{formatUsd(row.total_cost)}</td>
                              <td className="py-2 pr-3 text-right">{Number(row.total_input_tokens || 0).toLocaleString()}</td>
                              <td className="py-2 text-right">{Number(row.total_output_tokens || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Usage Logs</CardTitle>
                    <select className="border rounded px-2 py-1.5 text-sm bg-background w-44" value={llmPurpose} onChange={e => { setLlmPurpose(e.target.value); setLlmLogsPage(1); }}>
                      <option value="">All purposes</option>
                      <option value="chat">Chat</option>
                      <option value="image_generation">Image Generation</option>
                      <option value="video_generation">Video Generation</option>
                      <option value="web_search">Web Search</option>
                      <option value="canvas">Canvas</option>
                    </select>
                  </div>
                </CardHeader>
                <CardContent>
                  {llmLogsLoading ? (
                    <div className="flex justify-center py-8"><Spinner /></div>
                  ) : llmLogs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6">No usage logs yet.</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b text-left text-muted-foreground">
                            <th className="py-2 pr-3">Model</th><th className="py-2 pr-3">Purpose</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3 text-right">In Tokens</th><th className="py-2 pr-3 text-right">Out Tokens</th><th className="py-2 pr-3 text-right">Cost</th><th className="py-2 pr-3 text-right">Latency</th><th className="py-2">Date</th>
                          </tr></thead>
                          <tbody>
                            {llmLogs.map((log: any) => (
                              <tr key={log.id} className="border-b last:border-0">
                                <td className="py-2 pr-3 font-medium text-xs">{log.model}</td>
                                <td className="py-2 pr-3"><Badge variant="outline" className="text-xs">{log.purpose}</Badge></td>
                                <td className="py-2 pr-3"><Badge variant={log.status === 'success' ? 'success' : 'error'} className="text-xs">{log.status}</Badge></td>
                                <td className="py-2 pr-3 text-right">{Number(log.input_tokens).toLocaleString()}</td>
                                <td className="py-2 pr-3 text-right">{Number(log.output_tokens).toLocaleString()}</td>
                                <td className="py-2 pr-3 text-right font-medium">{formatUsd(log.total_cost_usd)}</td>
                                <td className="py-2 pr-3 text-right">{Number(log.latency_ms).toLocaleString()}ms</td>
                                <td className="py-2 whitespace-nowrap text-xs">{new Date(log.created_at).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {llmLogsTotal > 20 && (
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-sm text-muted-foreground">Page {llmLogsPage} of {Math.ceil(llmLogsTotal / 20)} ({llmLogsTotal} total)</span>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" disabled={llmLogsPage <= 1} onClick={() => setLlmLogsPage(p => p - 1)}>Prev</Button>
                            <Button size="sm" variant="outline" disabled={llmLogsPage * 20 >= llmLogsTotal} onClick={() => setLlmLogsPage(p => p + 1)}>Next</Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Add User Modal */}
      <Modal
        isOpen={showAddUserModal}
        onClose={() => { setShowAddUserModal(false); setAddUserEmail(''); setAddUserRole('member'); setAddUserError(null); setAddUserResult(null); }}
        title="Add User to Organization"
        description="If the user exists in the system, they will be added directly. Otherwise, an invite will be created."
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => { setShowAddUserModal(false); setAddUserEmail(''); setAddUserRole('member'); setAddUserError(null); setAddUserResult(null); }}>Cancel</Button>
            <Button onClick={handleAddUser} isLoading={addUserLoading} disabled={!addUserEmail.trim()}>Add User</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label="Email Address" type="email" placeholder="user@example.com" value={addUserEmail} onChange={(e) => setAddUserEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAddUser(); }} />
          <Select label="Role" value={addUserRole} options={[{ value: 'member', label: 'Member' }, { value: 'admin', label: 'Admin' }]} onValueChange={(v) => setAddUserRole(v)} />
          {addUserError && <div className="p-3 bg-red-50 border border-red-200 rounded-md"><p className="text-sm text-red-600">{addUserError}</p></div>}
          {addUserResult && (
            <div className={`p-3 rounded-md border ${addUserResult.type === 'member_added' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
              <p className={`text-sm ${addUserResult.type === 'member_added' ? 'text-green-700' : 'text-blue-700'}`}>{addUserResult.message}</p>
            </div>
          )}
        </div>
      </Modal>
    </PageContainer>
  );
}
